import React, { useEffect, useState, useMemo } from "react";
import Swal from "sweetalert2";
import {
  getFirestore,
  collection,
  query,
  where,
  addDoc,
  getDocs,
  writeBatch,
  deleteField,
} from "firebase/firestore";

const SidebarCalendario = ({
  filters,
  selectedFilters,
  toggleFixedFilter,
  isAddingCategory,
  setIsAddingCategory,
  newCategoryName,
  setNewCategoryName,
  handleAddCategory,
  sharedUsers,
  ownerUid,
  onCategoryDeleted,
}) => {
  /* ------------------ estados ------------------ */
  const [newCategoryColor, setNewCategoryColor] = useState(null);

  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    category: null,
  });

  /* 👉 NUEVOS estados para edición inline */
  const [editingCat, setEditingCat] = useState(null); // objeto en edición
  const [editingName, setEditingName] = useState(""); // valor editable

  /* ------------------ constantes ------------------ */
  const categoryColors = [
    "#8B5CF6",
    "#3B82F6",
    "#10B981",
    "#F59E0B",
    "#EF4444",
    "#EC4899",
    "#06B6D4",
  ];
  const fixedColors = useMemo(
    () => ({
      Festivos: "#8B5CF6",
      Eventos: "#6366F1",
      Cotizaciones: "#3B82F6",
    }),
    [] // ← referencia estable
  );

  /* ------------------ helpers ------------------ */
  const cancelEdit = () => setEditingCat(null);

  const saveEdit = async () => {
    const newName = editingName.trim();
    if (!newName) return cancelEdit();

    const duplicate = filters.some(
      (f) =>
        f.label.trim().toLowerCase() === newName.toLowerCase() &&
        f.id !== editingCat.id
    );
    if (duplicate) {
      return Swal.fire("Ya existe", "Ese nombre ya está en uso.", "warning");
    }

    try {
      const db = getFirestore();
      const oldSlug = editingCat.slug;
      const newSlug = newName.toLowerCase();
      const batch = writeBatch(db);

      /* 1️⃣  Buscar doc de la categoría */
      const filtQ = query(
        collection(db, "filtrosCategorias"),
        where("ownerId", "==", ownerUid),
        where("slug", "==", oldSlug)
      );
      const snapFilt = await getDocs(filtQ);

      if (snapFilt.empty) {
        // Era una categoría “por defecto” sin documento → lo creamos
        await addDoc(collection(db, "filtrosCategorias"), {
          label: newName,
          slug: newSlug,
          color: editingCat.color || "#D1D5DB",
          ownerId: ownerUid,
          invited: [],
          createdAt: new Date().toISOString(),
        });
      } else {
        // Existe doc → se actualiza
        snapFilt.forEach((d) =>
          batch.update(d.ref, { label: newName, slug: newSlug })
        );
      }

      /* 2️⃣  Si cambió slug, actualizar tareas que la usen */
      if (oldSlug !== newSlug) {
        const tQ = query(
          collection(db, "tareaCalendario"),
          where("ownerId", "==", ownerUid),
          where("categorySlug", "==", oldSlug)
        );
        (await getDocs(tQ)).forEach((d) =>
          batch.update(d.ref, { category: newName, categorySlug: newSlug })
        );
      }

      await batch.commit();
      Swal.fire("Actualizado", "Categoría modificada.", "success");

      await onCategoryDeleted(); // recarga datos
      cancelEdit();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo actualizar.", "error");
    }
  };

  const handleEditCategory = (category) => {
    setEditingCat(category);
    setEditingName(category.label);
    setContextMenu({ visible: false, x: 0, y: 0, category: null });
  };

  /* --------------- handleDeleteCategory (versión final) --------------- */
  const handleDeleteCategory = async (category) => {
    const confirm = await Swal.fire({
      title: "¿Eliminar categoría?",
      text: `Se eliminará “${category.label}” para todo el equipo. \
Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;

    try {
      const db = getFirestore();
      const slug = (category.slug || category.label).trim().toLowerCase();

      /* 1️⃣  Traemos TODOS los docs con ese slug  ------------------------- */
      const filtQ = query(
        collection(db, "filtrosCategorias"),
        where("slug", "==", slug)
      );
      const snapFilt = await getDocs(filtQ);

      /* 2️⃣  Traemos las tareas del owner que usen esa categoría ---------- */
      const tareasQ = query(
        collection(db, "tareaCalendario"),
        where("ownerId", "==", ownerUid),
        where("categorySlug", "==", slug)
      );
      const snapTareas = await getDocs(tareasQ);

      /* 3️⃣  Operación en lote ------------------------------------------- */
      const batch = writeBatch(db);

      //   ‣ eliminamos todas las definiciones de la categoría
      snapFilt.forEach((d) => batch.delete(d.ref));

      //   ‣ limpiamos la referencia en las tareas del owner
      snapTareas.forEach((d) =>
        batch.update(d.ref, {
          category: deleteField(),
          categorySlug: deleteField(),
        })
      );

      await batch.commit();

      Swal.fire("Eliminado", "Categoría eliminada con éxito.", "success");
      onCategoryDeleted(); // ← vuelve a cargar categorías + eventos
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo eliminar la categoría.", "error");
    }
  };

  /* ------------------ cerrar menú contextual fuera ------------------ */
  useEffect(() => {
    const outside = () =>
      contextMenu.visible &&
      setContextMenu({ visible: false, x: 0, y: 0, category: null });
    window.addEventListener("click", outside);
    return () => window.removeEventListener("click", outside);
  }, [contextMenu]);

  // 🔄  “Cotizaciones” deja de ser fija
  const categories = useMemo(() => {
    /* ①  ÚNICA categoría verdaderamente fija */
    const base = ["Festivos"]; // ← «Festivos» nunca se elimina

    /* ②  El resto se construye 100 % desde Firestore */
    const seen = new Set(base.map((b) => b.toLowerCase()));
    const dynamic = [];

    for (const f of filters) {
      const lbl = f.label.trim();
      const low = lbl.toLowerCase();
      if (!seen.has(low)) {
        seen.add(low);
        dynamic.push(lbl); // incluye Cotizaciones, Eventos, etc.
      }
    }

    /* ③  Mapeo final */
    return [...base, ...dynamic].map((lbl) => {
      const doc = filters.find(
        (f) => f.label.trim().toLowerCase() === lbl.toLowerCase()
      );

      return {
        id: doc?.id ?? lbl,
        label: lbl,
        slug: (doc?.slug ?? lbl).toLowerCase(),
        color: doc?.color ?? fixedColors[lbl] ?? "#D1D5DB",
        isFixed: lbl.toLowerCase() === "festivos", // ← sólo Festivos es fijo
      };
    });
  }, [filters, fixedColors]);

  /* ------------------ render ------------------ */
  return (
    <>
      <aside className="cal-sider w-[180px] flex-none overflow-hidden bg-white border border-[#E6EDFF] rounded-xl shadow-sm p-4">
        {/* "Todas" + botón "+" */}
        <div className="mb-5">
          <div className="flex items-center justify-between">
            <div
              className="flex items-center text-blue-600 cursor-pointer"
              onClick={() => toggleFixedFilter("Todos")}
            >
              <i className="bi bi-list text-lg mr-1"></i>
              <span className="text-sm font-medium">Todas</span>
            </div>
            <button
              className="inline-flex items-center justify-center h-5 w-5 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded"
              onClick={() => {
                setNewCategoryColor(
                  categoryColors[
                    Math.floor(Math.random() * categoryColors.length)
                  ]
                );
                setIsAddingCategory(true);
              }}
            >
              <i className="bi bi-plus-lg text-xs"></i>
            </button>
          </div>
        </div>

        {/* CATEGORÍAS */}
        <div>
          <h3 className="text-gray-500 uppercase font-semibold text-xs mb-3 tracking-wide">
            CATEGORÍAS
          </h3>

          {categories.map((cat) => {
            const selected = selectedFilters.includes(cat.label);
            const isEditing = editingCat && editingCat.slug === cat.slug;

            return (
              <div
                key={cat.slug}
                className="mb-3 flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-50"
                onClick={() => !isEditing && toggleFixedFilter(cat.label)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (isEditing || cat.isFixed) return;
                  setContextMenu({
                    visible: true,
                    x: e.clientX,
                    y: e.clientY,
                    category: cat,
                  });
                }}
              >
                <span
                  className="inline-flex items-center justify-center h-5 w-5 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: cat.color }}
                >
                  {selected && (
                    <i className="bi bi-check text-white text-xs"></i>
                  )}
                </span>

                {/* label o input */}
                {isEditing ? (
                  <input
                    autoFocus
                    className="text-sm font-medium w-full bg-white border-b border-gray-300 h-5 py-0 leading-5 focus:outline-none"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit();
                      if (e.key === "Escape") cancelEdit();
                    }}
                  />
                ) : (
                  <span
                    className={`text-sm font-medium ${
                      selected ? "text-blue-600" : "text-gray-700"
                    }`}
                  >
                    {cat.label}
                  </span>
                )}
              </div>
            );
          })}

          {/* Alta rápida de categoría */}
          {isAddingCategory && (
            <div className="mb-3 flex items-center gap-2 px-2 py-1">
              <span
                className="inline-flex items-center justify-center h-5 w-5 rounded-sm flex-shrink-0"
                style={{ backgroundColor: newCategoryColor || "#D1D5DB" }}
              />
              <input
                autoFocus
                type="text"
                placeholder="Nueva categoría…"
                className="w-full text-sm font-medium bg-white border-b border-gray-300 focus:outline-none"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === "Enter" && newCategoryName.trim()) {
                    await handleAddCategory(
                      newCategoryName.trim(),
                      newCategoryColor
                    );
                    setNewCategoryName("");
                    setIsAddingCategory(false);
                  }
                  if (e.key === "Escape") {
                    setNewCategoryName("");
                    setIsAddingCategory(false);
                  }
                }}
              />
            </div>
          )}
        </div>

        {/* COMPARTIDOS */}
        <div className="mt-8">
          <h3 className="text-gray-500 uppercase font-semibold text-xs mb-3 tracking-wide">
            COMPARTIDOS
          </h3>
          {sharedUsers.map((usr, i) => {
            const clr = [
              "#EF4444",
              "#06B6D4",
              "#F59E0B",
              "#10B981",
              "#6366F1",
              "#EC4899",
              "#3B82F6",
            ][i % 7];
            const nombre = usr.nombre.split(" ")[0];
            const apellido = usr.apellido.split(" ")[0];
            return (
              <div
                key={usr.id}
                className="mb-2 flex items-center gap-3 px-2 py-1"
              >
                <span
                  className="inline-block h-5 w-5 rounded-sm"
                  style={{ backgroundColor: clr }}
                />
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {(nombre + " " + apellido).toLowerCase()}
                </span>
              </div>
            );
          })}
        </div>
      </aside>

      {/* Menú contextual */}
      {contextMenu.visible && contextMenu.category && (
        <div
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            background: "#fff",
            border: "1px solid #ccc",
            boxShadow: "0 4px 12px rgba(0,0,0,.15)",
            zIndex: 9999,
            borderRadius: "8px",
            padding: "4px 0",
            minWidth: "150px",
          }}
          onClick={() =>
            setContextMenu({ visible: false, x: 0, y: 0, category: null })
          }
          onContextMenu={(e) => e.preventDefault()}
        >
          <button
            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
            onClick={() => handleEditCategory(contextMenu.category)}
          >
            <i className="bi bi-pencil-fill" /> Editar
          </button>
          <button
            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-red-600"
            onClick={() => handleDeleteCategory(contextMenu.category)}
          >
            <i className="bi bi-trash" /> Eliminar
          </button>
        </div>
      )}
    </>
  );
};

export default SidebarCalendario;
